<template>
	<div class="card" :class="card_class">
		<div class="card-background">
			<img class="metal-texture" src="https://inscribemag.com/wp-content/uploads-2/metal-texture-1.jpg">
			<img class="card-custom-background" v-if="card.background" :src="card.background">
		</div>

		<div class="card-info">
			<div class="card-frame" @click="holdCard">
				<div class="card-figure">
					<img v-lazy="iconURL(card.icon)" alt="">
				</div>
				<div class="card-type-label">
					<img :src="this.color_label_src" alt="">
				</div>
				<div class="card-id">
					{{ card.id.toString().padStart(3, '0') }}
				</div>
				<div class="card-add-loadout">+</div>
			</div>
			<div class="card-name">
				{{ card.name }}
			</div>
		</div>

		<div class="card-possess-info">
			Rank 5
			<input type="number" step="1" min="0" v-model="countR5Model">
			Others
			<input type="number" step="1" min="0" v-model="countR0Model">
		</div>

		<div class="card-stats grid-hidden">
			<div class="card-stats-title">Stats</div>
			<div class="card-stats-container">
				<div v-for="statName in [ 'atk', 'hp', 'def' ]" :key="statName"
					class="card-stat"
				>
					<span class="card-stat-name">{{ statName.toUpperCase() }}</span>
					<span class="card-stat-value">{{ card.stats[statName] }}</span>
				</div>
			</div>
		</div>

		<div v-for="(skill, i) in card.skills" :key="card.id+skill.name"
			class="card-skill"
			:class="{ [`skill-${i + 1}`]: true, selected: skill === selectedSkill }"
		>
			<img v-lazy="iconURL(skill.icon)" alt="" @click="selectSkill(skill)">
			<div class="skill-background"></div>
			<div class="requirement" :class="skill.def.type">
				<template v-if="skill.def.type == 'character'">
					<span>{{ skill.requirement }}</span>
				</template>
				<template v-else>
					<img v-for="letter in skill.requirement?.toString" :key="letter"
						:src="getTypeImg(letter)"
					>
				</template>
			</div>
			<div class="skill-definition grid-hidden">
				<div class="skill-name">{{ skill.name }} <span v-if="skill.def?.type == 'common'"> &nbsp; {{ cardSkillLevel }}</span></div>
				<div class="skill-description scrollable">
					<span>{{ skill.description }}</span>
				</div>
			</div>
		</div>
		<div v-for="i in (2 - card.skills.length)" :key="i"
			class="card-skill empty-skill"
			:class="`skill-${card.skills.length + i}`"
		>
			<div class="skill-background"></div>
		</div>
		<div class="skill-definition selection">
			<template v-if="selectedSkill">
				<div class="skill-name">{{ selectedSkill.name }}</div>
				<div class="skill-description scrollable">
					<span>{{ selectedSkill.description }}</span>
				</div>
			</template>
		</div>
	</div>
</template>

<script>
import {
	RANK_0_KEY,
	RANK_5_KEY,
	saveCard
} from '../utils/CardsGetter';

import {
	cardHeld
} from '@/utils/LoadoutList';

const RANKS = {
	0: 'B',
	1: 'A',
	2: 'S'
};

const TYPES_LABEL_SRCS = {
	0: '/6/66/Card_Red.png',
	1: '/0/0e/Card_Yellow.png',
	2: '/1/13/Card_Blue.png',
	3: '/6/6c/Card_Green.png'
};

export default {
	name: 'card',
	props: {
		card: {
			type: Object,
			required: true
		},
		'display-mode': {
			type: String,
			validator(value) {
				return [ 'grid', 'list' ].includes(value);
			},
			default: 'list'
		},
		countR0: {
			type: Number,
			required: true
		},
		countR5: {
			type: Number,
			required: true
		}
	},
	data() {
		return {
			// saveTimeoutID : set and used in "updateValue" method
			countR0Model: this.countR0,
			countR5Model: this.countR5,
			selectedSkill: this.card.skills[0] ?? null,
			showSkillDef: false // for res < 600
		};
	},
	computed: {
		cardSkillLevel() {
			return this.card.rank === 2 ? 'II ( III )' : 'I ( II )';
		},
		color_label_src() {
			return this.getTypeImg(this.card.type);
		},
		_debug() {
			return cardHeld.value;
		},
		card_class() {
			return {
				[`rank-${RANKS[this.card.rank]}`]: true,
				[`display-${this.displayMode}`]: true,
				'show-skill-def': this.showSkillDef,
				owned: this.card.owned[RANK_0_KEY] || this.card.owned[RANK_5_KEY]
			};
		}
	},
	watch: {
		countR0Model: 'updateR0',
		countR5Model: 'updateR5'
	},
	methods: {
		holdCard() {
			cardHeld.value = this.card;
		},
		updateValue(key, value) {
			this.$emit(key, value);
			clearTimeout(this.saveTimeoutID);
			this.saveTimeoutID = setTimeout(() => {
				saveCard(this.card);
			}, 2000);
		},
		updateR0() {
			this.updateValue('update:countR0', this.countR0Model);
		},
		updateR5() {
			this.updateValue('update:countR5', this.countR5Model);
		},
		selectSkill(skill) {
			const previousSelection = this.selectedSkill;
			this.selectedSkill = skill;
			if (window.innerWidth <= 600) {
				if (!this.showSkillDef) {
					this.showSkillDef = true;
					setTimeout(() => {
						this.$el.scrollIntoView({
							behavior: 'smooth',
							block: 'start'
						});
					}, 150);
				} else if (skill === previousSelection)
					this.showSkillDef = false;
			}
		},
		iconURL(pathToIcon) {
			return `https://static.wikia.nocookie.net/rockman_x_dive/images${pathToIcon}`;
		},
		getTypeImg(type) {
			if (typeof(type) == 'string')
				type = 'RYBG'.indexOf(type);
			return this.iconURL(TYPES_LABEL_SRCS[type]);
		}
	}
};
</script>

<style lang="scss">
$main-border: 4px;
$inner-border: 2px;

$top-square-width: 50px;
$card-height: 214px;
$half-card-height: calc(#{$card-height} / 2);

.card {
	width: 100%;
	height: $card-height;
	position: relative;

	display: grid;

	> * {
		z-index: 1;
	}

	.card-background {
		filter: drop-shadow(0 0 0.75rem rgba(0,0,0,.7));
		border: $main-border solid;
		border-radius: 30px 3px;
		overflow: hidden;
    height: 214px;
		.metal-texture {
			opacity: .15;
			width: 100%;
			min-width: 600px;
			background-repeat: repeat-y;
			position: absolute;
			inset: 0;
		}
		.card-custom-background {
			max-width: 500px;
			max-height: 100%;
			opacity: .7;
			object-fit: contain;
			margin-left: 50px;
		}
	}

	.card-info {
		grid-area: info-1;
		width: 130px;
		height: calc(100% - 20px);
		display: flex;
		justify-self: center;
		align-self: center;
		flex-direction: column;
    justify-content: space-around;
    align-content: center;
		justify-self: end;
		// card-frame [ figure - type - id ]
		// card-name
		.card-name {
			height: 40px;
			padding: 2px;
			line-height: 16px;
			font-size: 0.9rem;
			display: flex;
			justify-content: center;
			text-align: center;
			align-items: center;
			background: black;
			color: #dcdcdc;
			font-weight: bold;
			border: $inner-border solid #666;
		}

	}
	.card-stats,
	.card-possess-info {
		width: 100%;
		max-width: 80px;
		background: #424242;
	}

	.card-possess-info {
		grid-area: owned;
		justify-self: center;
		text-align: center;
		font-weight: 700;
		padding: 0 7px;
		color: white;
		border-radius: 6px;
    margin: 10px 0 5px;
		>input {
			width: 100%;
			background: black;
			color: white;
			text-align: center;
		}
	}

	.card-stats {
		grid-area: stats;
		justify-self: center;
    margin: 10px 10px 5px 0;
		color: white;
		border-radius: 8px;
		overflow: hidden;
		.card-stats-title {
			text-align: center;
			width: 100%;
			font-weight: bolder;
			font-size: 1.2rem;
			background: #0004;
			box-shadow: inset -1px -1px 14px rgba(0,0,0,.4);
		}
		.card-stats-container {
			.card-stat {
				display: flex;
				justify-content: center;
				.card-stat-name {
					width: 40%;
					font-weight: bold;
				}
				.card-stat-value {
					width: 40%;
					text-align: right;
				}
			}
		}
	}
	.card-skill {
		position: relative;
		color: white;
		&.skill-1 { grid-area: skill-1; }
		&.skill-2 { grid-area: skill-2; }
		&.empty-skill {
			.skill-background {
				box-shadow: inset -1px -1px 14px #000;
				opacity: 0.5;
			}
		}
		.skill-background {
			z-index: -1;
			position: absolute;
			inset: 0;
			left: 85px;
			background: #424242;
		}
		.skill-background,
		.requirement {
			border-top-left-radius: 10px;
			border-bottom-left-radius: 10px;
		}
	}
	.skill-definition {
		grid-area: skill-definition;
		display: flex;
		flex-direction: column;
		height: 100%;
		flex: auto;
		>* {
			padding: 0 5px;
		}
		.skill-name {
			min-height: 30px;
			display: flex;
			align-items: center;
			font-weight: 600;
			background: linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.1));
			box-shadow: inset -1px -1px 14px #0006;
		}
		.skill-description {
			padding: 4px;
			text-overflow: ellipsis;
			white-space: pre-line;
		}
	}

	@media (max-width: 767px) {
		grid-template-areas:
			"info-1  owned   stats  skill-def"
			"info-1 skill-1 skill-2 skill-def";
		grid-template-columns: 145px 90px 90px auto;
		grid-template-rows: 50% 50%;
    justify-items: center;
		.skill-definition.selection {
			grid-area: skill-def;
			background: #424242;
			color: white;
			box-shadow: inset -1px -1px 14px #0006;
			border-radius: 4px;
			border-bottom-right-radius: 20px;
			margin: 10px;
			margin-left: 0;
			height: calc(100% - 20px);
			overflow: hidden;
			.skill-name {
				padding: 5px;
			}
			.skill-description {
				padding: 5px;
			}
		}
		.card-skill {
			position: relative;
			width: calc(min(100%, 90px));
			display: flex;
			flex-direction: column-reverse;
			align-items: center;
			justify-content: flex-end;
			.skill-background {
				display: none;
			}
			.requirement {
				width: calc(min(calc(100% - 10px), 80px));
				background: rgba(0, 0, 0, 0.2);
				border-radius: 14px;
				margin-bottom: 3px;
				box-shadow: inset 1px 2px 4px rgba(0,0,0,50%);
				background: #424242;
				color: white;
				&.common {
					display: grid;
					grid-template-columns: 1fr 1fr 1fr;
					grid-template-rows: 28px;
					align-items: center;
					justify-items: center;
					>img {
						transition: all ease-in-out 150ms;
						height: 23px;
						width: 23px;
					}
				}
				&.character {
					font-size: 85%;
					line-height: 12px;
					text-align: center;
					height: 28px;
					font-weight: 600;
					> span {
						display: flex;
						width: 100%;
						height: 100%;
						justify-content: center;
						align-items: center;
						padding: 4px;
					}
				}
			}
			>img {
				transition: all ease-in-out 150ms;
				aspect-ratio: 1 / 1;
				width: calc(100% - 30px);
				filter: drop-shadow(0 5px 0.3rem rgba(0, 0, 0, 0.7));
			}
			&.selected {
				>img {
					border: 1px solid #cbfffa;
				}
			}
			&:not(.selected):not(.empty-skill) {
				>img {
					transform: scale(0.95);
					filter: brightness(80%);
				}
				cursor: pointer;
				@media (hover: hover) and (pointer: fine) {
					&:hover {
						>img {
							transform: scale(1);
							filter: brightness(100%);
						}
					}
				}
			}
			.skill-definition {
				display: none;
			}
		}
	}

	@media (max-width: 599px) {
		grid-template-areas:
			"info-1     owned      stats  "
			"info-1    skill-1    skill-2 "
			"skill-def skill-def skill-def";
		grid-template-columns: 145px 1fr 1fr;
		grid-template-rows: $half-card-height $half-card-height 0px;
    transition: all ease-in-out 150ms;
		.card-skill {
			align-self: start;
			>img {
				cursor: pointer;
			}
			.requirement {
				margin-top: 0;
			}
		}
		.skill-definition {
			&.selection {
				transition: all ease-in-out 150ms;
				margin: 0 40px;
				height: 0px;
				border: 2px solid transparent;
				color: white;
			}
		}
		&:not(.show-skill-def) {
			.card-skill {
				>img {
					border: none;
					transform: scale(0.95);
					filter: brightness(80%);
				}
				@media (hover: hover) and (pointer: fine) {
					&:hover {
						>img {
							transform: scale(1);
							filter: brightness(100%);
						}
					}
				}
			}
		}
		&.show-skill-def {
			$skill-def-height: calc(#{$card-height} * 0.65);
			height: calc(#{$card-height} + #{$skill-def-height});
			grid-template-columns: 145px 1fr 1fr;
			grid-template-rows: $half-card-height $half-card-height $skill-def-height;
			.skill-definition {
				&.selection {
					border:2px solid #777;
					border-top: 0;
					height: $skill-def-height;
				}
			}
		}
	}

	@media (min-width: 769px) {
		grid-template-areas:
			"info-1 owned skill-1"
			"info-1 stats skill-2";
		grid-template-columns: 145px 90px 1fr;
		grid-template-rows: 50% 50%;
		.skill-definition.selection {
			display: none;
		}

		.card-skill {
			display: flex;
			margin: 10px;
			margin-left: 0;
			flex-direction: row;
			align-items: center;
			border-radius: 5px;
			overflow: hidden;
			.requirement {
				width: 35px;
				height: 100%;
				background: rgba(0,0,0,0.2);
				box-shadow: inset 1px 2px 4px rgba(0,0,0,50%);
				padding: 4px;
				&.common {
					display: grid;
					grid-template-rows: 1fr 1fr 1fr;
					flex-direction: column;
					justify-content: space-around;
					align-items: center;
					>img {
						height: 25px;
						width: 25px;
					}
				}
				&.character {
					writing-mode: vertical-rl;
					transform: rotate(180deg);
					border-radius: 0;
					border-top-right-radius: 10px;
					border-bottom-right-radius: 10px;
					font-size: 100%;
					line-height: 15px;
					text-align: center;
					> span {
						display: flex;
						width: 100%;
						height: 100%;
						justify-content: center;
						align-items: center;
					}
				}
			}
			>img {
				height: 75px;
				width: 75px;
				margin: 0 5px;
				filter: drop-shadow(0 5px 0.3rem rgba(0, 0, 0, 0.7));
			}
			&.skill-1 {
				margin-bottom: 5px;
			}
			&.skill-2 {
				margin-top: 5px;
				border-bottom-right-radius: 20px;
			}
		}
	}
}

@mixin color-pack($lighten-color, $main-color, $darken-color) {
	.card-background {
		background: linear-gradient($lighten-color, $main-color);
		border-color: $lighten-color;
	}
	.card-frame {
		border-color: $main-color;
		background      : radial-gradient($lighten-color 8px, $main-color 5px);
		background-size : 22px 22px;
		background-position: -10px -10px;
	}
	.card-name {
		background: $darken-color;
	}
}

$lighten-color-B: #3cbae2;
$main-color-B : #005b90;
$darken-color-B: #042651;

$lighten-color-A: #e98fff;
$main-color-A : #581a6a;
$darken-color-A: #2d0451;

$lighten-color-S: #fff340;
$main-color-S : #86650c;
$darken-color-S: #3a2a04;

.card-background {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	z-index: 0;
}
.card-frame {
	touch-action: none;
	cursor: pointer;
	position: relative;
	border-width: $main-border;
	border-style: solid;
	border-radius: 5px;
	padding-bottom: 100%;
	.card-figure {
		position: absolute;
		top: 0;
		bottom: 0;
		width: 100%;
		height: 100%;
		overflow: hidden;
		box-shadow: inset 0 0 16px black;
		> img {
			aspect-ratio: 1 / 1;
			object-fit: cover;
			transition: all 225ms ease-in-out;
			width: 100%;
			height: 100%;
			filter: drop-shadow(10px 16px 3px rgba(0,0,0,0.6));
		}
	}
	.card-type-label {
		position: absolute;
		top: -15px;
		left: -15px;
		width: $top-square-width;
		height: $top-square-width;
		>img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}
	.card-id, .card-add-loadout {
		display: flex;
		align-items: center;
		justify-content: center;
		background: black;
		color: #dcdcdc;
		width: 35px;
		height: 20px;
		position: absolute;
		right: 0;
		border-left: 1px solid #fff7;
		border-bottom: 1px solid #fff7;
	}
	.card-id {
		top: 0;
	}
	.card-add-loadout {
		bottom: 0;
	}
	@media (hover: hover) and (pointer: fine) {
		&:hover {
				.card-figure {
					> img {
						transform: scale(1.05);
					}
				}
		}
	}
}
.rank-B {
	@include color-pack($lighten-color-B, $main-color-B, $darken-color-B);
}
.rank-A {
	@include color-pack($lighten-color-A, $main-color-A, $darken-color-A);
}
.rank-S {
	@include color-pack($lighten-color-S, $main-color-S, $darken-color-S);
}
</style>
